import React, { useEffect, useState } from 'react';

import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Avatar,
  Tooltip,
  IconButton,
  Drawer,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material';
import {
  Logout,
  ChevronLeft,
  ChevronRight,
  FolderOutlined as FolderIcon,
  Menu as Bars3Icon,
  GroupOutlined as UsersIcon,
  AutoAwesomeOutlined as SparklesIcon,
  Close as XMarkIcon,
  VerifiedUserOutlined as ShieldCheckIcon,
  DashboardOutlined as DashboardIcon,
  ExitToAppOutlined as DepartureIcon,
  AssignmentIndOutlined as IncomingViewIcon,
  FactCheckOutlined as AuditLogIcon,
  ArchiveOutlined as ArchiveIcon,
  CreditCardOutlined as BillingIcon,
  SupportAgentOutlined as SupportIcon,
  HomeOutlined as HomeIcon,
  WorkspacesOutlined as WorkspacesOutlinedIcon,
  DarkModeOutlined as DarkModeIcon,
  LightModeOutlined as LightModeIcon,
  ListAltOutlined as TemplateIcon,
} from '@mui/icons-material';
import { getUserInitials, getAvatarColor } from '../../helpers/avatarHelper';
import { getCompanyDisplayName } from '../../helpers/companyHelper';
import { NavLink, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ProjectLogo from '../../components/commons/ProjectLogo';
import { AI_ASSISTANT_ENABLED, LEVEL1, LEVEL2, THEME_TOGGLE_ENABLED } from '../../constants/constant';
import { purgeStoredState } from 'redux-persist';
import {
  persistAuthConfig,
  persistUserConfig,
  persistHandoverConfig,
  persistHandoverTaskConfig,
  persistHandoverCommentConfig,
} from '../../store';
import { RootState } from '../../store';
import { useAppSelector, useAppDispatch } from '../../hooks/reduxHooks';
import { openChatGPTAssistant } from '../../redux/ai/aiSlice';
import { includes } from 'lodash';
import ChatGPTSpeedDial from '../../ai/ChatGPTSpeedDial';
import { projectConfig } from '../../config/projectConfig';
import { getToken, removeToken } from '../../helpers/tokenStorage';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { useGetMyCompanyQuery } from '../../redux/company/companyApi';
import {
  canAccessLockedAdminRoute,
  getCompanyAccessSummaryFromQuery,
  getSeatSummaryFromQuery,
  isAdminRole,
  isBillingLockedStatus,
} from '../../helpers/companyAccessHelper';
import { getEffectiveUserRole, isPlatformOwner } from '../../helpers/platformAccessHelper';
import { WorkspaceBillingBanner } from '../../components/commons/WorkspaceBillingBanner';
import { AiUsageBanner } from '../../components/commons/AiUsageBanner';
import { WorkspaceLockedScreen } from '../../components/commons/WorkspaceLockedScreen';
import { firebaseAuth } from '../../config/firebaseClient';
import { signOut as signOutFirebaseUser } from 'firebase/auth';

type SideNavigationItem = {
  name: string;
  to: string;
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  onClick?: () => void;
};

type SideNavigationGroup = {
  label: string;
  items: SideNavigationItem[];
};

const navButtonSx = (theme: any, open: boolean) => ({
  py: 1.5,
  px: open ? 3 : 0,
  color: theme.HEPalette.sidebar.textSecondary,
  justifyContent: open ? 'flex-start' : 'center',
  '&:hover': {
    backgroundColor: theme.HEPalette.sidebar.hover,
    color: theme.HEPalette.sidebar.text,
    '& .MuiListItemIcon-root': {
      color: theme.HEPalette.sidebar.text,
    },
  },
  '&.active': {
    backgroundColor: theme.HEPalette.sidebar.active,
    color: theme.HEPalette.sidebar.text,
    '& .MuiListItemIcon-root': {
      color: theme.HEPalette.sidebar.text,
    },
  },
});

const navIconSx = (theme: any, open: boolean) => ({
  minWidth: 0,
  mr: open ? 2.5 : 0,
  justifyContent: 'center',
  color: theme.HEPalette.sidebar.textSecondary,
  display: 'flex',
  alignItems: 'center',
});

const renderNavigationItems = (
  items: SideNavigationItem[],
  theme: any,
  open: boolean,
  onNavigate?: () => void
) =>
  items.map(item => (
    <ListItem key={item.name} disablePadding>
      <ListItemButton
        component={item.to === '#' ? 'button' : NavLink}
        to={item.to === '#' ? undefined : item.to}
        onClick={() => {
          if (item.onClick) item.onClick();
          if (onNavigate) onNavigate();
        }}
        sx={navButtonSx(theme, open)}
      >
        <ListItemIcon sx={navIconSx(theme, open)}>
          <item.icon style={{ width: 24, height: 24 }} />
        </ListItemIcon>
        {open && (
          <ListItemText
            primary={item.name}
            primaryTypographyProps={{
              fontSize: '0.95rem',
              fontWeight: 500,
              color: 'inherit',
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  ));

const renderNavigationGroup = (
  group: SideNavigationGroup,
  theme: any,
  open: boolean,
  onNavigate?: () => void
) => (
  <Box key={group.label} sx={{ mb: 1.5 }}>
    {open && (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          px: 3,
          pb: 0.75,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: theme.HEPalette.sidebar.textSecondary,
          fontWeight: 700,
        }}
      >
        {group.label}
      </Typography>
    )}
    <List sx={{ width: '100%', py: 0 }}>
      {renderNavigationItems(group.items, theme, open, onNavigate)}
    </List>
  </Box>
);

const SideNavigation = ({
  onNavigate,
  open,
  billingLocked,
}: {
  onNavigate?: () => void;
  open: boolean;
  billingLocked: boolean;
}) => {
  const userAuth = useAppSelector((state: RootState) => state.auth.userAuth);
  const effectiveUserRole = getEffectiveUserRole(userAuth);
  const isLevel1 = includes(LEVEL1, effectiveUserRole);
  const isLevel2 = includes(LEVEL2, effectiveUserRole);
  const isSuperAdmin = effectiveUserRole === 'superAdmin';
  const isWorkspaceAdmin = isAdminRole(effectiveUserRole);
  const isPlatformOwnerUser = isPlatformOwner(userAuth);
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // Check if AI feature is enabled
  const isAIEnabled = useAppSelector((state: RootState) => state.health.features.ai);
  const showAIAssistant = AI_ASSISTANT_ENABLED && isAIEnabled;

  const workGroup = [
    {
      name: 'My Work',
      to: '/my-work',
      icon: HomeIcon,
    },
    isLevel2 && {
      name: 'Manager Dashboard',
      to: '/dashboard',
      icon: DashboardIcon,
    },
    {
      name: 'Handover Documents',
      to: './handovers',
      icon: FolderIcon,
    },
    isLevel1 && {
      name: 'Departures',
      to: '/departure',
      icon: DepartureIcon,
    },
    isLevel1 && {
      name: 'Incoming Views',
      to: '/incoming-view',
      icon: IncomingViewIcon,
    },
  ].filter(Boolean) as SideNavigationItem[];

  const teamGroup = [
    { name: 'Members', to: './users', icon: UsersIcon },
  ].filter(Boolean) as SideNavigationItem[];

  const oversightGroup = [
    isLevel2 && {
      name: 'User Permissions',
      to: '/auth/update/role',
      icon: ShieldCheckIcon,
    },
    isLevel2 && {
      name: 'Handover Archive',
      to: '/handovers/archive',
      icon: ArchiveIcon,
    },
    isLevel2 && {
      name: 'Departure Template',
      to: '/departure-template',
      icon: TemplateIcon,
    },
    isLevel2 && {
      name: 'Audit Log',
      to: '/audit-log',
      icon: AuditLogIcon,
    },
    showAIAssistant && {
      name: 'AI Assistant',
      to: '#',
      icon: SparklesIcon,
      onClick: () => dispatch(openChatGPTAssistant()),
    },
  ].filter(Boolean) as SideNavigationItem[];

  const workspaceGroup = [
    isSuperAdmin && {
      name: 'Billing',
      to: '/billing',
      icon: BillingIcon,
    },
  ].filter(Boolean) as SideNavigationItem[];

  const internalGroup = [
    isPlatformOwnerUser && {
      name: 'Trial Workspaces',
      to: '/internal/trials',
      icon: WorkspacesOutlinedIcon,
    },
  ].filter(Boolean) as SideNavigationItem[];

  const lockedWorkspaceGroup = [
    isWorkspaceAdmin && {
      name: 'Billing',
      to: '/billing',
      icon: BillingIcon,
    },
  ].filter(Boolean) as SideNavigationItem[];

  const navigationGroups: SideNavigationGroup[] = billingLocked
    ? [{ label: 'Workspace', items: lockedWorkspaceGroup }].filter(
        group => group.items.length > 0
      )
    : isPlatformOwnerUser && !userAuth?.companyId
      ? [
          { label: 'Internal', items: internalGroup },
          { label: 'Workspace', items: workspaceGroup },
        ].filter(group => group.items.length > 0)
    : [
        { label: 'Work', items: workGroup },
        { label: 'Team', items: teamGroup },
        { label: 'Oversight', items: oversightGroup },
        { label: 'Internal', items: internalGroup },
        { label: 'Workspace', items: workspaceGroup },
      ].filter(group => group.items.length > 0);

  const supportNavigation = billingLocked
    ? []
    : [
    {
      name: 'Support',
      to: '/support',
      icon: SupportIcon,
    },
  ].filter(Boolean) as SideNavigationItem[];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: open ? 0 : 0, py: 0 }}>
        {navigationGroups.map(group =>
          renderNavigationGroup(group, theme, open, onNavigate)
        )}
      </Box>
      {supportNavigation.length ? (
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <Divider sx={{ borderColor: theme.HEPalette.sidebar.border, mx: open ? 2 : 1.5, mb: 1.5 }} />
          {open && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 3,
                pb: 0.75,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: theme.HEPalette.sidebar.textSecondary,
                fontWeight: 700,
              }}
            >
              Support
            </Typography>
          )}
          <List sx={{ width: '100%', py: 0 }}>
            {renderNavigationItems(supportNavigation, theme, open, onNavigate)}
          </List>
        </Box>
      ) : null}
    </Box>
  );
};

const UserNavigation = () => {
  const userAuth = useAppSelector((state: RootState) => state.auth.userAuth);
  const user = useAppSelector((state: RootState) => state.user.userDocProfileData);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onLogoutHandler = async () => {
    try {
      const token = getToken();
      await fetch(`${projectConfig.apiEndpoints.auth}/logout`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    try {
      await signOutFirebaseUser(firebaseAuth);
    } catch (error) {
      console.error('Firebase client logout error:', error);
    }
    removeToken();
    await purgeStoredState(persistAuthConfig);
    await purgeStoredState(persistUserConfig);
    await purgeStoredState(persistHandoverConfig);
    await purgeStoredState(persistHandoverTaskConfig);
    await purgeStoredState(persistHandoverCommentConfig);
    window.location.href = '/';
    return false;
  };

  // Avatar logic as in Profile.tsx
  const initials = getUserInitials(user?.firstName, user?.lastName, userAuth?.email);
  const avatarColor = getAvatarColor(
    user?.avatarColor,
    user?.firstName || user?.lastName || userAuth?.email,
    'primary.main'
  );
  const companyName = getCompanyDisplayName(userAuth, user);
  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || userAuth?.email;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Tooltip title="Account settings">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <Avatar
              sx={{
                width: 45,
                height: 45,
                bgcolor: avatarColor,
                fontSize: 22,
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.15))',
              mt: 1.5,
              minWidth: 200,
              borderRadius: 2,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                mr: 1.5,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: avatarColor,
                width: 40,
                height: 40,
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700} noWrap>
                {displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {userAuth?.email}
              </Typography>
              {companyName && (
                <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5 }} noWrap>
                  {companyName}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Divider />
        <MenuItem
          component={Link}
          to="/profile"
          onClick={handleClose}
          sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Avatar
            sx={{
              bgcolor: avatarColor,
              width: 32,
              height: 32,
              fontSize: 16,
              fontWeight: 600,
              mr: 1.5,
            }}
          >
            {initials}
          </Avatar>
          <ListItemText
            primary="Profile"
            primaryTypographyProps={{
              variant: 'body1',
              fontWeight: 500,
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={onLogoutHandler}
          sx={{
            py: 1.5,
            px: 2,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Logout fontSize="small" color="action" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              variant: 'body1',
              fontWeight: 500,
            }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

const Logo = () => {
  const theme = useTheme();

  return (
    <Box
      component={Link}
      to="/my-work"
      sx={{
        display: 'flex',
        alignItems: 'center',
        textDecoration: 'none',
        color: theme.HEPalette.sidebar.text,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(35, 31, 26, 0.94)' : 'rgba(255, 255, 255, 0.95)',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          padding: 0.75,
          boxShadow:
            theme.palette.mode === 'dark' ? '0 12px 28px rgba(12, 9, 7, 0.45)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
          minWidth: 48,
          minHeight: 48,
        }}
      >
        <ProjectLogo size="sidebar" />
      </Box>
      <Box
        sx={{
          fontSize: '1.25rem',
          fontWeight: 600,
          color: theme.HEPalette.sidebar.text,
        }}
      >
        {projectConfig.branding.title}
      </Box>
    </Box>
  );
};

const SidebarContent = ({
  onClose,
  open,
  billingLocked,
}: {
  onClose?: () => void;
  open: boolean;
  billingLocked: boolean;
}) => {
  const theme = useTheme();
  const userAuth = useAppSelector((state: RootState) => state.auth.userAuth);
  const user = useAppSelector((state: RootState) => state.user.userDocProfileData);
  const companyName = getCompanyDisplayName(userAuth, user);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.HEPalette.sidebar.background,
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          height: 64,
          px: open ? 3 : 0,
          backgroundColor: theme.HEPalette.sidebar.background,
          borderBottom: `1px solid ${theme.HEPalette.sidebar.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {open ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Logo />
            {onClose && (
              <IconButton
                onClick={onClose}
                sx={{
                  display: { md: 'none' },
                  color: theme.HEPalette.sidebar.text,
                }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </IconButton>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(35, 31, 26, 0.94)' : 'rgba(255, 255, 255, 0.95)',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1.5,
              padding: 0.75,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 12px 28px rgba(12, 9, 7, 0.45)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
              width: 48,
              height: 48,
            }}
          >
            <ProjectLogo size="sidebar" />
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          py: 2,
        }}
      >
        <SideNavigation onNavigate={onClose} open={open} billingLocked={billingLocked} />
      </Box>

      {/* Footer */}
      {open && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.HEPalette.sidebar.border}`,
            backgroundColor: theme.HEPalette.sidebar.backgroundDark,
          }}
        >
          {companyName && (
            <Box
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
                border: `1px solid ${theme.HEPalette.sidebar.border}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: theme.HEPalette.sidebar.textSecondary,
                  mb: 0.5,
                }}
              >
                Workspace
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.HEPalette.sidebar.text,
                  fontWeight: 600,
                  wordBreak: 'break-word',
                }}
              >
                {companyName}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              fontSize: '0.75rem',
              color: theme.HEPalette.sidebar.textSecondary,
              textAlign: 'center',
            }}
          >
            {projectConfig.branding.copyright}
          </Box>
        </Box>
      )}
    </Box>
  );
};

type MainLayoutProps = {
  children: React.ReactNode;
};

const DRAWER_WIDTH = 280;
const MINI_DRAWER_WIDTH = 72;

export const MainLayout = ({ children }: MainLayoutProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const theme = useTheme();
  const { mode, toggleColorMode } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const userAuth = useAppSelector((state: RootState) => state.auth.userAuth);
  const effectiveUserRole = getEffectiveUserRole(userAuth);
  const isPlatformOwnerUser = isPlatformOwner(userAuth);
  const {
    data: companyDetails,
    error: companyError,
  } = useGetMyCompanyQuery(undefined, {
    skip: !userAuth?.companyId,
  });
  const companyAccess = getCompanyAccessSummaryFromQuery(companyDetails, companyError);
  const seatSummary = getSeatSummaryFromQuery(companyDetails);
  const lockedBilling = isBillingLockedStatus(companyAccess);
  const lockedRouteAllowed = canAccessLockedAdminRoute(location.pathname, effectiveUserRole);
  const billingSetupRequired = companyAccess?.billingStatus === 'inactive';
  const isWorkspaceAdmin = isAdminRole(effectiveUserRole);
  const workspaceBillingLocked = billingSetupRequired || lockedBilling;
  const shouldShowLockedScreen = (billingSetupRequired || lockedBilling) && !lockedRouteAllowed;
  const shouldRedirectToBilling =
    workspaceBillingLocked &&
    isWorkspaceAdmin &&
    !location.pathname.startsWith('/billing');
  const supportEmail = projectConfig.branding.companyInfo.email;
  const welcomeStorageKey = userAuth?.email ? `he-first-login:${userAuth.email.toLowerCase()}` : null;
  const welcomePrimaryRoute = isPlatformOwnerUser
    ? '/internal/trials'
    : includes(LEVEL2, effectiveUserRole)
      ? '/departure'
      : '/my-work';

  useEffect(() => {
    if (!userAuth?.email || !welcomeStorageKey) {
      return;
    }

    if (localStorage.getItem(welcomeStorageKey) === 'seen') {
      return;
    }

    setWelcomeOpen(true);
  }, [userAuth?.email, welcomeStorageKey]);

  const handleCloseWelcome = () => {
    if (welcomeStorageKey) {
      localStorage.setItem(welcomeStorageKey, 'seen');
    }
    setWelcomeOpen(false);
  };

  const handleWelcomePrimaryAction = () => {
    handleCloseWelcome();
    navigate(welcomePrimaryRoute);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    setDesktopOpen(!desktopOpen);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <SidebarContent onClose={handleDrawerToggle} open={true} billingLocked={workspaceBillingLocked} />
      </Drawer>

      {/* Desktop Mini Variant Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: desktopOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: desktopOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.HEPalette.sidebar.border}`,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
        open={desktopOpen}
      >
        <SidebarContent open={desktopOpen} billingLocked={workspaceBillingLocked} />
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          maxHeight: '100vh',
          overflow: 'hidden',
          width: { xs: '100%', md: 'auto' },
        }}
      >
        {/* App Bar */}
        <AppBar
          position="static"
          elevation={1}
          sx={{
            backgroundColor: theme.HEPalette.sidebar.background,
            color: theme.HEPalette.sidebar.text,
            zIndex: theme => theme.zIndex.drawer - 1,
            flexShrink: 0,
          }}
        >
          <Toolbar>
            {/* Desktop Drawer Toggle */}
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={handleDesktopDrawerToggle}
              edge="start"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'block' },
              }}
            >
              {desktopOpen ? <ChevronLeft /> : <ChevronRight />}
            </IconButton>

            {/* Mobile Menu Toggle */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <Bars3Icon style={{ width: 24, height: 24 }} />
            </IconButton>

            <Box sx={{ flexGrow: 1 }} />

            {THEME_TOGGLE_ENABLED && (
              <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                <IconButton color="inherit" onClick={toggleColorMode} sx={{ mr: 1 }}>
                  {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
            )}

            <UserNavigation />
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            p: { xs: 2, sm: 3 },
          }}
        >
          <WorkspaceBillingBanner
            accessSummary={companyAccess}
            seatSummary={seatSummary}
            supportEmail={supportEmail}
            canManageBilling={isWorkspaceAdmin}
          />
          <AiUsageBanner company={companyDetails?.company} />
          {shouldRedirectToBilling ? (
            <Navigate to="/billing" replace />
          ) : shouldShowLockedScreen ? (
            <WorkspaceLockedScreen
              accessSummary={companyAccess}
              supportEmail={supportEmail}
              isAdmin={isAdminRole(effectiveUserRole)}
              canManageBilling={isWorkspaceAdmin}
            />
          ) : (
            children
          )}
        </Box>
      </Box>

      {AI_ASSISTANT_ENABLED && <ChatGPTSpeedDial />}

      <Dialog
        open={welcomeOpen}
        onClose={handleCloseWelcome}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundImage:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(30,22,17,0.98) 0%, rgba(22,17,14,0.98) 100%)'
                : undefined,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: '2rem', fontWeight: 700 }}>
          Welcome to Handover Engine
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {includes(LEVEL2, effectiveUserRole)
              ? 'Open Departures to start or manage a handover.'
              : isPlatformOwnerUser
                ? 'Open Trial Workspaces to provision a new company trial.'
                : 'Open My Work to continue your tasks.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseWelcome} sx={{ textTransform: 'none' }}>
            Maybe later
          </Button>
          <Button variant="contained" onClick={handleWelcomePrimaryAction} sx={{ textTransform: 'none', borderRadius: 999 }}>
            {isPlatformOwnerUser
              ? 'Open Trial Workspaces'
              : includes(LEVEL2, effectiveUserRole)
                ? 'Open Departures'
                : 'Open My Work'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
